import { useEffect, useMemo, useState } from 'react';
import { useLocation, useParams } from 'react-router-dom';
import { Swiper, SwiperSlide } from 'swiper/react';
import type { Swiper as SwiperType } from 'swiper';
import avatarIcon from '../assets/pets/avatar.svg';
import birthdayCakeIcon from '../assets/pets/birthday-cake.svg';
import cardDecorationIcon from '../assets/pets/card-decoration.svg';
import checkIcon from '../assets/icon-check.svg';
import minusIcon from '../assets/icon-minus.svg';
import ErrorDisplay from '../components/ErrorDisplay';
import ImagePreviewer from '../components/ImagePreviewer';
import type {
  Attachment,
  CareStatusItem,
  NfcApiResponse,
  PetMetric,
  PetProfile,
  RawPetContent,
  RawPetPayload,
} from '../types';

import 'swiper/css';

const isBrowser = typeof window !== 'undefined';

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null;
}

function sanitizeString(value: unknown): string {
  return typeof value === 'string' ? value.trim() : '';
}

function sanitizeStringArray(value: unknown): string[] {
  if (!Array.isArray(value)) return [];
  return value
    .filter((item): item is string => typeof item === 'string')
    .map((item) => item.trim())
    .filter(Boolean);
}

function sanitizeStringList(value: unknown): string[] {
  if (Array.isArray(value)) {
    return sanitizeStringArray(value);
  }

  const normalized = sanitizeString(value);
  return normalized ? [normalized] : [];
}

function normalizeCareStatusLabel(value: string): string {
  if (value.startsWith('已接种')) {
    return value.slice('已接种'.length);
  }

  if (value.startsWith('已完成')) {
    return value.slice('已完成'.length);
  }

  return value;
}

function toCareStatusItem(value: string): CareStatusItem | null {
  const normalized = sanitizeString(value);
  if (!normalized || normalized === '未知') return null;

  if (normalized === '暂未完成') {
    return {
      label: normalized,
      isComplete: false,
    };
  }

  if (normalized.startsWith('已接种') || normalized.startsWith('已完成')) {
    return {
      label: normalizeCareStatusLabel(normalized) || normalized,
      isComplete: true,
    };
  }

  return null;
}

function mapGender(value: string): string {
  switch (value) {
    case 'male':
      return '公';
    case 'female':
      return '母';
    case 'unknown':
      return '未知';
    default:
      return value || '未知';
  }
}

function mapSterilized(value: string): string {
  switch (value) {
    case 'yes':
      return '已绝育';
    case 'no':
      return '未绝育';
    case 'unknown':
      return '未知';
    default:
      return value || '未知';
  }
}

function mapHealthStatus(value: string): string {
  switch (value) {
    case 'vaccinated':
      return '已接种';
    case 'unvaccinated':
      return '暂未完成';
    case 'unknown':
      return '未知';
    default:
      return value || '未知';
  }
}

function mapCertificateType(value: string): string {
  switch (value) {
    case 'immunization':
      return '免疫证';
    case 'dog_license':
      return '犬只证';
    case 'chip':
      return '宠物芯片编号';
    case 'other':
      return '其他';
    default:
      return value || '未知';
  }
}

function formatAgeFromBirthday(value: string): string {
  const birthday = sanitizeString(value);
  if (!birthday) return '未知';

  const parsedDate = new Date(birthday);
  if (Number.isNaN(parsedDate.getTime())) return '未知';

  const today = new Date();
  if (parsedDate > today) return '未知';

  let years = today.getFullYear() - parsedDate.getFullYear();
  let months = today.getMonth() - parsedDate.getMonth();

  if (today.getDate() < parsedDate.getDate()) {
    months -= 1;
  }

  if (months < 0) {
    years -= 1;
    months += 12;
  }

  const birthdayUtc = Date.UTC(
    parsedDate.getFullYear(),
    parsedDate.getMonth(),
    parsedDate.getDate(),
  );
  const todayUtc = Date.UTC(
    today.getFullYear(),
    today.getMonth(),
    today.getDate(),
  );
  const days = Math.floor((todayUtc - birthdayUtc) / (1000 * 60 * 60 * 24));

  if (years < 0) return '未知';
  if (years === 0 && months === 0) return `${days}天`;
  if (years === 0) return `${months}个月`;
  if (months === 0) return `${years}岁`;

  return `${years}岁\n${months}个月`;
}

function buildCareStatus(content: RawPetContent): CareStatusItem[] {
  const items: CareStatusItem[] = [];
  const healthStatuses = sanitizeStringList(content.healthStatus)
    .map((status) => mapHealthStatus(status))
    .map((status) => toCareStatusItem(status))
    .filter((status): status is CareStatusItem => Boolean(status));

  if (healthStatuses.length > 0) {
    items.push(...healthStatuses);
  }

  return items;
}

function parseRawPetContent(content: unknown): RawPetContent | null {
  if (typeof content === 'string') {
    try {
      const parsed: unknown = JSON.parse(content);
      return parseRawPetContent(parsed);
    } catch {
      return null;
    }
  }

  if (!isRecord(content)) return null;

  return {
    nickname: sanitizeString(content.nickname),
    birthday: sanitizeString(content.birthday),
    breed: sanitizeString(content.breed),
    gender: sanitizeString(content.gender),
    sterilized: sanitizeString(content.sterilized),
    healthStatus: sanitizeStringList(content.healthStatus),
    ownerName: sanitizeString(content.ownerName),
    ownerPhone: sanitizeString(content.ownerPhone),
    ownerAvatar: sanitizeString(content.ownerAvatar),
    addressRegion: sanitizeString(content.addressRegion),
    addressStreet: sanitizeString(content.addressStreet),
    tags: sanitizeStringArray(content.tags),
    otherInfo: sanitizeString(content.otherInfo),
    petPhotos: sanitizeStringArray(content.petPhotos),
    certificateType: sanitizeString(content.certificateType),
    certificateNo: sanitizeString(content.certificateNo),
    certificatePhotos: sanitizeStringArray(content.certificatePhotos),
  };
}

function resolvePetContent(
  payload: unknown,
): { id: string; content: RawPetContent } | null {
  if (!payload || !isRecord(payload)) return null;

  if ('data' in payload) {
    const nested = (payload as RawPetPayload).data ?? null;
    return resolvePetContent(nested);
  }

  const rawPayload = payload as RawPetPayload;
  if (sanitizeString(rawPayload.type) !== 'pet') return null;

  const content = parseRawPetContent(rawPayload.content);
  if (!content) return null;

  return {
    id: sanitizeString(rawPayload.id),
    content,
  };
}

function mapPetContentToProfile(
  id: string,
  content: RawPetContent,
): PetProfile {
  const ownerAddress =
    `${content.addressRegion || ''}${content.addressStreet || ''}`.trim();
  const age = formatAgeFromBirthday(content.birthday || '');

  return {
    id,
    name: content.nickname || '未命名宠物',
    birthday: content.birthday || '不详',
    metrics: [
      { label: '性别', value: mapGender(content.gender || '') },
      { label: '绝育', value: mapSterilized(content.sterilized || '') },
      { label: '年龄', value: age },
    ],
    breed: content.breed || '未知品种',
    ownerName: content.ownerName || '未填写',
    ownerPhone: content.ownerPhone || '未填写',
    ownerAddress: ownerAddress || undefined,
    ownerAvatar: content.ownerAvatar || undefined,
    tags: (content.tags || []).map((tag) =>
      tag.startsWith('#') ? tag : `#${tag}`,
    ),
    notes: content.otherInfo || '',
    careStatus: buildCareStatus(content),
    certificateType: mapCertificateType(content.certificateType || ''),
    certificateNumber: content.certificateNo || '暂无',
    images: content.petPhotos || [],
    certificatePhotos: content.certificatePhotos || [],
  };
}

function MetricCard({ value, label }: PetMetric) {
  return (
    <div className="relative aspect-square w-full max-w-[5.6rem] min-w-0 overflow-hidden rounded-[16px] bg-[#FFF599]">
      <img
        src={cardDecorationIcon}
        className="pointer-events-none absolute bottom-[-2px] right-[-4px] h-[4rem] w-[4rem] opacity-80"
      />
      <div className="relative z-10 h-full px-1 text-center">
        <div className="absolute inset-x-0 top-0 bottom-5 flex items-center justify-center px-1">
          <div className="w-full max-h-[3rem] overflow-hidden whitespace-pre-wrap break-all text-[1.2rem] font-semibold leading-[1.5rem] text-[#333333]">
            {value}
          </div>
        </div>
        <div className="absolute bottom-4 left-0 right-0 text-center text-[1rem] leading-none text-[rgba(0,0,0,0.3)]">
          {label}
        </div>
      </div>
    </div>
  );
}

function useResolvedId() {
  const params = useParams<{ id?: string }>();
  const location = useLocation();

  return useMemo(() => {
    if (params.id) return params.id;
    if (!isBrowser) return '';
    const query = new URLSearchParams(location.search);
    return query.get('id') || '';
  }, [location.search, params.id]);
}

function PetPage() {
  const id = useResolvedId();
  const [status, setStatus] = useState<'loading' | 'ready' | 'error'>(
    'loading',
  );
  const [errorMessage, setErrorMessage] = useState('');
  const [pet, setPet] = useState<PetProfile | null>(null);
  const [activeImageIndex, setActiveImageIndex] = useState(0);
  const [previewItems, setPreviewItems] = useState<Attachment[]>([]);
  const [selectedPreviewImage, setSelectedPreviewImage] = useState<
    string | null
  >(null);

  useEffect(() => {
    if (!id) {
      setStatus('error');
      setErrorMessage('NFC ID 是必需的');
      return;
    }

    const controller = new AbortController();

    async function load() {
      setStatus('loading');
      setErrorMessage('');

      try {
        const response = await fetch(
          `/api/nfc/content?id=${encodeURIComponent(id)}`,
          {
            headers: {
              'Content-Type': 'application/json',
            },
            signal: controller.signal,
          },
        );

        if (!response.ok) {
          throw new Error(
            `获取宠物信息失败: ${response.status} ${response.statusText}`,
          );
        }

        const payload: NfcApiResponse = await response.json();
        if (typeof payload.code === 'number' && payload.code !== 0) {
          throw new Error(
            payload.message || `服务返回错误（代码: ${payload.code}）`,
          );
        }

        const resolved = resolvePetContent(payload.data ?? null);
        if (!resolved) {
          throw new Error('未找到可用的宠物信息');
        }

        setPet(mapPetContentToProfile(resolved.id || id, resolved.content));
        setStatus('ready');
      } catch (error) {
        if (controller.signal.aborted) return;
        setPet(null);
        setStatus('error');
        setErrorMessage(
          error instanceof Error ? error.message : '加载宠物信息时发生未知错误',
        );
      }
    }

    load();
    return () => controller.abort();
  }, [id]);

  useEffect(() => {
    setActiveImageIndex(0);
  }, [pet?.id]);

  const handleSlideChange = (swiper: SwiperType) => {
    setActiveImageIndex(swiper.activeIndex);
  };

  const petImageItems = useMemo<Attachment[]>(() => {
    if (!pet) return [];

    return pet.images.map((imageUrl, index) => ({
      name: `${pet.name}-${index + 1}`,
      url: imageUrl,
      contentType: 'image/*',
    }));
  }, [pet]);

  const certificateImageItems = useMemo<Attachment[]>(() => {
    if (!pet) return [];

    return pet.certificatePhotos.map((imageUrl, index) => ({
      name: `${pet.name}-certificate-${index + 1}`,
      url: imageUrl,
      contentType: 'image/*',
    }));
  }, [pet]);

  const openPreview = (items: Attachment[], imageUrl: string) => {
    setPreviewItems(items);
    setSelectedPreviewImage(imageUrl);
  };

  const closePreview = () => {
    setSelectedPreviewImage(null);
    setPreviewItems([]);
  };

  const certificatePhoto = pet?.certificatePhotos[0] ?? '';

  if (!id) {
    return (
      <ErrorDisplay
        message="缺少 NFC ID"
        description="请通过扫描 NFC 标签或使用包含 id 参数的链接访问此页面。"
      />
    );
  }

  if (status === 'error') {
    return (
      <ErrorDisplay
        message={errorMessage || '加载宠物信息失败'}
        description="请稍后重试，或确认 NFC 标签对应的数据已正确配置。"
        onRetry={isBrowser ? () => window.location.reload() : undefined}
      />
    );
  }

  if (status === 'loading' || !pet) {
    return (
      <div
        className="min-h-screen flex items-center justify-center text-white"
        style={{
          backgroundImage: "url('/images/bg.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'center',
          backgroundRepeat: 'no-repeat',
        }}
      >
        <p className="text-base">宠物信息加载中...</p>
      </div>
    );
  }

  return (
    <div className="relative min-h-screen text-[#333333]">
      <div
        aria-hidden="true"
        className="absolute inset-0 bg-cover bg-top bg-no-repeat"
        style={{
          backgroundImage: "url('/images/bg.jpg')",
          backgroundSize: 'cover',
          backgroundPosition: 'top center',
          backgroundRepeat: 'no-repeat',
        }}
      />
      <div className="relative z-10 flex min-h-screen w-full flex-col pt-3">
        <div className="mb-3 px-4 text-center text-[1rem] font-medium text-black">
          宠物名牌
        </div>

        <section className="w-full flex-1">
          <div className="relative h-[450px] w-full max-h-[300px] overflow-hidden bg-[#F5F5F5]">
            <Swiper
              onSlideChange={handleSlideChange}
              className="pet-image-swiper h-full w-full"
            >
              {pet.images.map((imageUrl, index) => (
                <SwiperSlide
                  key={`${imageUrl}-${index}`}
                  className="!flex h-full items-center justify-center"
                >
                  <div className="flex h-full w-full items-stretch justify-center overflow-hidden">
                    <img
                      src={imageUrl}
                      alt={`${pet.name}-${index + 1}`}
                      className="block h-full w-auto max-w-none cursor-pointer object-contain"
                      onClick={() => openPreview(petImageItems, imageUrl)}
                    />
                  </div>
                </SwiperSlide>
              ))}
            </Swiper>
            {pet.images.length > 1 && (
              <div className="absolute bottom-3 left-1/2 z-10 flex -translate-x-1/2 gap-1.5">
                {pet.images.map((_, index) => (
                  <span
                    key={index}
                    className={`h-2 w-2 rounded-full ${index === activeImageIndex ? 'bg-[#9B9B9B]' : 'bg-white/80'}`}
                  />
                ))}
              </div>
            )}
          </div>

          <div className="px-4 pb-6 pt-6">
            <section className="flex flex-col gap-4">
              <div className="flex flex-col gap-3">
                <h1 className="text-[1.8rem] font-semibold leading-none text-[#333333]">
                  {pet.name}
                </h1>
                <div className="text-[1rem] text-[#777777]">{pet.breed}</div>
                <div className="flex items-center gap-2 text-[#777777]">
                  <img src={birthdayCakeIcon} alt="生日" className="h-6 w-6" />
                  <span className="text-[1rem] font-medium leading-none">
                    {pet.birthday || '不详'}
                  </span>
                </div>
              </div>

              <div className="grid w-full grid-cols-3 justify-items-center gap-3 pb-1">
                {pet.metrics.map((metric) => (
                  <MetricCard key={metric.label} {...metric} />
                ))}
              </div>

              <section className="rounded-[12px] bg-[#F5F5F5] px-[1rem] py-3">
                <div className="flex items-center gap-4">
                  <div className="flex h-[54px] w-[54px] shrink-0 items-center justify-center overflow-hidden rounded-full bg-[#CFC9FF]">
                    {pet.ownerAvatar ? (
                      <img
                        src={pet.ownerAvatar}
                        alt={pet.ownerName}
                        className="h-full w-full object-cover"
                      />
                    ) : (
                      <img
                        src={avatarIcon}
                        alt="主人头像"
                        className="h-[36px] w-[36px]"
                      />
                    )}
                  </div>
                  <div className="min-w-0 flex-1">
                    <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                      <span className="text-[1.1rem] font-semibold text-black">
                        {pet.ownerName}
                      </span>
                      <span className="text-[1rem] text-[#42B6FF]">
                        ({pet.ownerPhone})
                      </span>
                    </div>
                    {pet.ownerAddress ? (
                      <p className="mt-2 break-words text-[0.9rem] leading-[1.3rem] text-[#777777]">
                        {pet.ownerAddress}
                      </p>
                    ) : null}
                  </div>
                </div>
              </section>

              <section>
                <h2 className="text-[1.2rem] font-semibold text-black">
                  其他信息
                </h2>
                {pet.tags.length > 0 || pet.notes ? (
                  <>
                    {pet.tags.length > 0 && (
                      <div className="mt-3 flex flex-wrap gap-2">
                        {pet.tags.map((tag) => (
                          <span
                            key={tag}
                            className="rounded-[8px] bg-[#F5F5F5] px-2 py-1 text-[0.9rem] text-black/55"
                          >
                            {tag}
                          </span>
                        ))}
                      </div>
                    )}
                    {pet.notes && (
                      <p className="mt-3 text-[0.9rem] leading-[1.3rem] text-[#777777]">
                        {pet.notes}
                      </p>
                    )}
                  </>
                ) : (
                  <p className="mt-3 text-[0.9rem] leading-[1.3rem] text-[#777777]">
                    暂无
                  </p>
                )}
              </section>

              <div className="border-t border-[#E5E5E5]" />

              <section>
                <h2 className="text-[1.2rem] font-semibold text-black">
                  健康与安全信息
                </h2>
                <div className="mt-3 leading-6 text-[#7A7A7A] space-y-1">
                  <div className="flex items-start">
                    <div className="shrink-0 text-[1rem]">护理状态：</div>
                    <div className="flex flex-wrap gap-2">
                      {pet.careStatus.length > 0 ? (
                        pet.careStatus.map((item) => (
                          <span
                            key={`${item.label}-${item.isComplete ? 'complete' : 'pending'}`}
                            className="inline-flex items-center gap-2 rounded-[20px] bg-[#F5F5F5] px-[6px] py-[2px] pr-[8px] text-[1rem] text-[#333333]"
                          >
                            <span
                              className="flex h-[1.5rem] w-[1.5rem] items-center justify-center rounded-full"
                              style={{
                                backgroundColor: item.isComplete
                                  ? '#52C41A'
                                  : '#FFE60A',
                              }}
                            >
                              <img
                                src={item.isComplete ? checkIcon : minusIcon}
                                alt={item.isComplete ? '已完成' : '暂未完成'}
                                className="h-[1rem] w-[1rem]"
                              />
                            </span>
                            <span className="text-[0.9rem]">{item.label}</span>
                          </span>
                        ))
                      ) : (
                        <span className="text-[0.9rem] text-[#777777]">
                          暂无
                        </span>
                      )}
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="shrink-0 text-[1rem]">保险备案编号：</div>
                    <div className="min-w-0 text-[1rem] text-[#333333] break-words">
                      {pet.certificateNumber}
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="shrink-0 text-[1rem]">证件类型：</div>
                    <div className="min-w-0 text-[1rem] text-[#333333] break-words">
                      {pet.certificateType}
                    </div>
                  </div>

                  <div className="flex items-start">
                    <div className="shrink-0 text-[1rem]">证件编号：</div>
                    <div className="min-w-0 text-[1rem] text-[#333333] break-words">
                      {pet.certificateNumber}
                    </div>
                  </div>

                  {certificatePhoto ? (
                    <div className="flex">
                      <div className="shrink-0 text-[1rem]">证件照片：</div>
                      <div className="flex items-end gap-3">
                        <img
                          src={certificatePhoto}
                          alt="证件照片"
                          className="h-[58px] w-[92px] cursor-pointer rounded-xl object-cover"
                          onClick={() =>
                            openPreview(certificateImageItems, certificatePhoto)
                          }
                        />
                        <button
                          type="button"
                          onClick={() =>
                            openPreview(certificateImageItems, certificatePhoto)
                          }
                          className="border-none bg-transparent p-0 text-[1rem] text-[#333333]"
                        >
                          点击查看大图
                        </button>
                      </div>
                    </div>
                  ) : null}
                </div>
              </section>
            </section>
          </div>
        </section>
      </div>

      {selectedPreviewImage ? (
        <ImagePreviewer
          selectedImage={selectedPreviewImage}
          carouselItems={previewItems}
          onClose={closePreview}
          onChange={setSelectedPreviewImage}
        />
      ) : null}
    </div>
  );
}

export default PetPage;
